import {
  Box,
  Button,
  Stack,
  Zoom
} from "@mui/material";

import { BOTTOM_NAVBAR_HEIGHT, MAX_WIDTH } from "../../theme/gaimcontrolTheme";
import AppbarMenu from "../../components/appbarmenu";
import BottomNavBar from "../../components/bottomnavbar";
import TextBoxComponent from "../../components/textbox";
import { useEffect, useState } from "react";
import ThoughtsPanel from "../../components/thoughtspanel";
import ThoughtFeelingsPanel from "../../components/thoughtfeelingspanel";

export interface ThoughtCard {
  thought: string;
  feeling?: string;
  alternative?: string;
};

const TextContent = [
  [
    {
      type: 'header',
      content: 'Your Inner Compass'
    },
    {
      type: 'text',
      content: "Your thoughts and feelings influence your behaviour like the magnetic " +
        "field influences the direction of your inner compass."
    },
    {
      type: 'text',
      content: "Sometimes, certain thoughts can act like disturbance, pulling your " +
        "compass off course, and lead you to gambling."
    },
    {
      type: 'text',
      content: 'Let\'s identify these thoughts and explore how they might be influencing your feelings.'
    }
  ],
  [
    {
      type: 'header',
      content: "Let's take a closer look"
    },
    {
      type: 'text',
      content: "Thoughts like \"I'll just play a little to relax\", \"I need to win back my losses\", "+
        "or \"I have a system, and it will work\" can act as disturbances, pulling you towards gambling.",
    },
    {
      type: 'text',
      content: "Think back to the last time you gambled, and recall the thoughts that led you to that decision."
    },
    {
      type: 'text',
      content: 'Let\'s start listing them, one by one!'
    }
  ],
]

const exerciseCards: ThoughtCard[] = [
  { thought: 'Other'},
  { thought: "I’ll just play for a little while" },
  { thought: "I deserve to gamble." },
  { thought: "One bet won’t harm me." },
  { thought: "How can I win if I don’t play?" },
  { thought: "“No one loses on their birthday." },
  { thought: "Gambling is an easy way to earn money." },
  { thought: "My gambling is under control, I just was unlucky." },
  { thought: "I’m smart; I have a system to beat the odds." },
  { thought: "Gambling will be the solution to my problems." },
  { thought: "Now, it will pay everything back." },
  { thought: "Gambling makes me feel better." },
  { thought: "Someday I’ll score a really big win." },
  { thought: "I can win it back." },
  { thought: "I can’t lose on my birthday." },
  { thought: "I am smarter than the other gamblers." },
  { thought: "I deserve to gamble after a hard day." },
  { thought: "I need to relax now." },
]

const feelingsList: string[] = [
  "Hope",
  "Excitement",
  "Anticipation of relief",
  "Anticipation of euphoria",
  "Anxiety",
  "Impatience",
  "Restlessness",
  "Fear of missing out",
  "Other"
]

function DistortedThoughtsExercise() {
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [displayContent, setDisplayContent] = useState<boolean>(false);
  const [selectedCards, setSelectedCards] = useState<ThoughtCard[]>([]);
  const [cardPool, setCardPool] = useState<ThoughtCard[]>(exerciseCards);

  const onAddCard = (card: ThoughtCard) => {
    if (card.thought !== "Other") {
      setSelectedCards((prev) => [...prev, card]);
      setCardPool((prev) => prev.filter((c) => c.thought !== card.thought));
    } else {
      const name = prompt("What did you think in particular?")
      if (name && name !== 'Other') {
        setSelectedCards((prev) => [...prev, {thought: name}]);
      }
    }
  }

  const onRemoveCard = (card: ThoughtCard) => {
    setSelectedCards((prev) => prev.filter((c) => c.thought !== card.thought));
    setCardPool((prev) => [card, ...prev]);
  }

  const onAddFeeling = ( cardIndex: number, feeling: string ) => {
    var setFeeling = feeling
    if (feeling === "Other") {
      const name = prompt("What this thought makes you feel?")
      if (!name || name === 'Other') {
        return
      }
      setFeeling = name
    }
    
    const updatedCards = selectedCards.map((card, index) => {
      if (index === cardIndex) {
        return { ...card, feeling: setFeeling };
      }
      return card
     })
     setSelectedCards(updatedCards)
  }

  const onRemoveFeeling = ( cardIndex: number ) => {
    const updatedCards = selectedCards.map((card, index) => {
      if (index === cardIndex) {
        return { ...card, feeling: undefined };
      }
      return card
     })
     setSelectedCards(updatedCards)
  }

  useEffect(() => {
    setDisplayContent(false);
    setTimeout(() => {
      setDisplayContent(true);
    }, 100);
  }, [currentPageIndex])

  return (
      <Stack
        flexGrow={1}
        maxWidth={MAX_WIDTH}
        width={'100%'}
        alignItems={'center'}
        justifyContent={'center'}
        alignSelf={'center'}
      >
        <AppbarMenu />
        <Stack flexGrow={1} display={'flex'} width={'100%'}>
          <Stack
            flexGrow={1}
            width={'100%'}
            spacing={2}
            alignItems={'center'}
            sx={{
              background: "url('exercises/thoughts/compass.svg')",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              overflowY: 'auto'
            }}
          >
            <Box flexGrow={1} />
            <Zoom in={displayContent}>
              {
                currentPageIndex < 2 ? 
                <Stack display={'flex'} flexDirection={'column'} alignItems={'center'} spacing={2}>
                  <TextBoxComponent content={TextContent[currentPageIndex % TextContent.length]}/>
                  <Button variant="contained" sx={{px: 10}} onClick={()=> setCurrentPageIndex(currentPageIndex + 1)}>Next</Button>
                </Stack>
                : currentPageIndex === 2 ?
                <Box>
                  <ThoughtsPanel
                    selectedCards={selectedCards}
                    cardPool={cardPool}
                    onAddCard={onAddCard}
                    onRemoveCard={onRemoveCard}
                    onNext={()=> setCurrentPageIndex(currentPageIndex + 1)}
                  />
                </Box>
                :
                <Box>
                  <ThoughtFeelingsPanel
                    card={selectedCards[currentPageIndex - 3]}
                    feelings={feelingsList}
                    onSetFeeling={(feeling: string) => onAddFeeling(currentPageIndex - 3, feeling)}
                    onRemoveFeeling={() => onRemoveFeeling(currentPageIndex - 3)}
                  />
                </Box>
              }
            </Zoom>
            <Box flexGrow={1} />
          </Stack>
        </Stack>
        <Box height={BOTTOM_NAVBAR_HEIGHT} />
        <BottomNavBar />
      </Stack>
  );
}

export default DistortedThoughtsExercise;
